import React from "react"
import styled from "styled-components"
import { motion } from "framer-motion"
import { useTranslation } from "react-i18next"

const Layer = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  justify-content: flex-end;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 11;
`

const InnerLayer = styled(motion.div)`
  flex-direction: column;
  background-color: var(--background-color);
  margin-left: auto;
  padding: 0 20px 50px;
  width: 100%;
  max-width: 400px;
`

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  padding: 18px 23px;
  margin-bottom: 40px;
`

const Body = styled.div`
  --scrollbar-width: 15px;
  height: 100%;
  overflow-y: auto;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
`

const variants = {
  open: { opacity: 1, display: "flex" },
  closed: { opacity: 0, transitionEnd: { display: "none" } },
}

const innerVariants = {
  open: { opacity: 1, x: 0, display: "flex" },
  closed: { opacity: 0, x: "40px", transitionEnd: { display: "none" } },
}

const LayerMenu = ({ children, show, setShow }) => {
  const { t } = useTranslation()
  const layerRef = React.useRef(null)

  const closeOnOutsideClick = React.useCallback(
    (e, ref) => {
      if (e.target === ref.current) {
        setShow(false)
      }
    },
    [setShow]
  )

  React.useEffect(() => {
    const handleEsc = event => {
      if (event.keyCode === 27) {
        setShow(false)
      }
    }
    window.addEventListener("keydown", handleEsc)

    return () => {
      window.removeEventListener("keydown", handleEsc)
    }
  }, [setShow])

  return (
    <Layer
      animate={show ? "open" : "closed"}
      variants={variants}
      transition={{ duration: 0.4 }}
      initial={false}
      ref={layerRef}
      onClick={e => closeOnOutsideClick(e, layerRef)}
    >
      <InnerLayer
        animate={show ? "open" : "closed"}
        variants={innerVariants}
        transition={{ duration: 0.6 }}
        initial={false}
      >
        <Header>
          <div
            role="button"
            tabIndex="0"
            style={{
              display: "flex",
              alignItems: "center",
              fontWeight: "400",
              gap: "8px",
            }}
            onKeyDown={() => {
              setShow(false)
            }}
            onClick={() => {
              setShow(false)
            }}
          >
            {t("menu.close")}
            <svg aria-label="Close" viewBox="0 0 24 24" height="18" width="18">
              <path
                fill="none"
                stroke="var(--text-color)"
                strokeWidth="2"
                d="m3 3 18 18M3 21 21 3"
              />
            </svg>
          </div>
        </Header>
        <Body>
          <Content>{children}</Content>
        </Body>
      </InnerLayer>
    </Layer>
  )
}

export default LayerMenu
